import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { documentTitleForPath, pageNameForPath } from '@/utils/route-title';

/**
 * Names the current page — in the browser tab, and out loud.
 *
 * Two gaps this closes, both consequences of the app being a single page that
 * never renamed itself:
 *
 * 1. **Every tab read "Command Bridge".** Only the public portal ever set
 *    `document.title`. An operator running Map, Tasks, Incidents and Shelters
 *    side by side — which is the normal way this product is used — had four
 *    identical tabs to pick between, and a browser history where every entry
 *    carried the same label.
 *
 * 2. **Navigation was silent to assistive tech.** A server-rendered app
 *    announces the new page because the document changes; a client-side router
 *    swaps the DOM under a title that never moves, so a screen-reader user
 *    following a link hears nothing and has to go looking for what changed.
 *
 * The live region is deliberately empty on first render. Announcing the landing
 * page would talk over the page load itself, which the user did not navigate to.
 *
 * Mounted in every shell that owns routes — the tenant app, the field console
 * and the platform admin. The first version of this went into `MainLayout`
 * only, which left three quarters of the app still unnamed; `/admin` and
 * `/field` are siblings of `MainLayout`, not children of it.
 */
interface RouteAnnouncerProps {
  /**
   * Suffix for the tab title. The platform-admin console is a different product
   * surface from the tenant app and says so, rather than presenting itself as
   * the same thing under a different URL.
   */
  appName?: string;
}

export default function RouteAnnouncer({ appName = 'Command Bridge' }: RouteAnnouncerProps = {}) {
  const { pathname } = useLocation();
  const [announcement, setAnnouncement] = useState('');
  const isFirstRender = useRef(true);

  useEffect(() => {
    document.title = documentTitleForPath(pathname, appName);

    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setAnnouncement(pageNameForPath(pathname));
  }, [pathname, appName]);

  return (
    <div aria-live="polite" aria-atomic="true" className="sr-only">
      {announcement}
    </div>
  );
}
